import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Block, fdatetime, btnGhost } from './ui';

const KIND = {
  broker: 'Venta sin broker', cobro: 'Cobro a revisar', fecha: 'Fecha a confirmar', bricks: 'Bricks a decidir',
  fhct_origen: 'Origen del pago FHCT', fhct_socio: 'Compra sin socio', comision_sin_beneficiario: 'Comisión pagada sin beneficiario',
};
// Historial de cambios: acciones y entidades en español (si no está en el mapa, se muestra la original)
const ACTION = {
  create: 'Alta', update: 'Cambio', delete: 'Borrado', mark_paid: 'Marcada pagada', mark_unpaid: 'Marcada sin pagar',
  import: 'Importación', link: 'Vinculación', unlink: 'Desvinculación',
};
const ENTITY = {
  brick_adjustment: 'Ajuste de bricks', expense: 'Gasto', member: 'Socio', operation: 'Operación', broker: 'Broker',
  fhct: 'Aportación FHCT', settings: 'Ajustes', commission: 'Comisión', cdm: 'CDM', platform_user: 'Usuario de la plataforma',
};

export default function CdmPendientes() {
  const { data, error, reload } = useLoad(() => dashboardApi.cdmPending(), []);
  const [tab, setTab] = useState('pendientes');
  const [showResolved, setShowResolved] = useState(false);
  const items = useMemo(() => (data?.pending || []).filter((p) => showResolved || !p.resolved), [data, showResolved]);
  if (!data) return <Loading error={error} />;
  const resolve = async (p, resolved) => {
    try { await dashboardApi.cdmResolve({ ref: p.ref, kind: p.kind, resolved }); reload(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-4" data-testid="cdm-pendientes">
      {/* Samuel, 14/10/2026: los pendientes de la importación se dejan para más adelante */}
      <p className="rounded-xl border border-amber-300/20 bg-amber-300/[0.05] px-4 py-2.5 text-sm text-amber-200">En pruebas: los pendientes de la importación del CDM se revisarán más adelante. Las cifras pueden cambiar.</p>
      <div className="flex gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1 w-fit">
        {[['pendientes', `Pendientes (${data.pending.filter((p) => !p.resolved).length})`], ['correcciones', `Correcciones de la importación (${data.import_log.length})`], ['historial', 'Historial de cambios']].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-lg text-sm ${tab === k ? 'bg-[#c9a84c]/15 text-[#c9a84c]' : 'text-gray-400'}`}>{v}</button>
        ))}
      </div>
      {tab === 'pendientes' && (
        <Block title="Lo que falta decidir o completar" right={<label className="text-xs text-gray-400 flex items-center gap-2"><input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Ver resueltos</label>}>
          <Table rowKey="_k" rows={items.map((p, i) => ({ ...p, _k: `${p.kind}-${p.ref}-${i}` }))} cols={[
            { key: 'kind', label: 'Tipo', render: (p) => <Badge tone={p.kind === 'comision_sin_beneficiario' ? 'bad' : 'warn'}>{KIND[p.kind] || p.kind}</Badge> },
            { key: 'ref', label: 'Ref.', className: 'text-white font-medium' },
            { key: 'text', label: 'Qué hay que hacer', render: (p) => (<div>{p.text}{p.web_sponsor && <p className="text-xs text-gray-400">Patrocinador según la web: {p.web_sponsor}</p>}</div>) },
            { key: 'resolved', label: '', noSort: true, render: (p) => <button onClick={() => resolve(p, !p.resolved)} className={btnGhost}>{p.resolved ? 'Reabrir' : 'Hecho'}</button> },
          ]} empty="No hay nada pendiente" />
          <p className="text-xs text-gray-400">Las ventas sin broker se resuelven solas al asignar el broker en «Miembros»; el origen de los FHCT, al elegirlo en «FHCT y bricks».</p>
        </Block>
      )}
      {tab === 'correcciones' && (
        <Table rowKey="_i" rows={data.import_log.map((x, i) => ({ ...x, _i: i }))} cols={[
          { key: 'entity', label: 'Hoja' },
          { key: 'ref', label: 'Ref.', className: 'text-white' },
          { key: 'field', label: 'Campo' },
          { key: 'before', label: 'Antes', render: (x) => <span className="text-xs">{typeof x.before === 'object' && x.before ? JSON.stringify(x.before) : String(x.before ?? '—')}</span> },
          { key: 'after', label: 'Ahora', render: (x) => <span className="text-xs">{typeof x.after === 'object' && x.after ? JSON.stringify(x.after) : String(x.after ?? '—')}</span> },
          { key: 'reason', label: 'Motivo' },
        ]} />
      )}
      {tab === 'historial' && (
        <Table rowKey="_a" rows={(data.audit || []).map((a, i) => ({ ...a, _a: `${a.at}-${i}` }))} cols={[
          { key: 'at', label: 'Cuándo', render: (a) => fdatetime(a.at) },
          { key: 'by', label: 'Quién' },
          { key: 'action', label: 'Acción', render: (a) => ACTION[a.action] || a.action },
          { key: 'entity', label: 'Qué', render: (a) => ENTITY[a.entity] || a.entity },
          { key: 'ref', label: 'Ref.', render: (a) => <span className="text-xs">{a.ref}</span> },
        ]} empty="Sin cambios todavía" />
      )}
    </div>
  );
}
